/**
 * ChatHistory Component
 * Sidebar/drawer for chat history management
 */

import React from "react";
import {
        Box,
        Typography,
        IconButton,
        Tooltip,
        Button,
        List,
        ListItem,
        ListItemButton,
        ListItemText,
        useTheme,
        alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import HistoryIcon from "@mui/icons-material/History";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { formatHistoryDateTime } from "./utils/messageUtils";


/**
 * Strip common markdown syntax for plain-text previews.
 */
const stripMarkdown = (text) => {
        if (!text) return "";
        return text
                .replace(/\*\*(.+?)\*\*/g, "$1")
                .replace(/\*(.+?)\*/g, "$1")
                .replace(/__(.+?)__/g, "$1")
                .replace(/_(.+?)_/g, "$1")
                .replace(/~~(.+?)~~/g, "$1")
                .replace(/`(.+?)`/g, "$1")
                .replace(/^#{1,6}\s+/gm, "")
                .replace(/^[\-*+]\s+/gm, "")
                .replace(/^\d+\.\s+/gm, "")
                .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
                .replace(/\n{2,}/g, " ")
                .trim();
};

const ChatHistory = ({
        sessions,
        selectedChatId,
        isOpen,
        variant = "sidebar",
        historyWidth = 280,
        onSelectChat,
        onDeleteChat,
        onCreateChat,
        onToggle,
        disableInteractions,
}) => {
        const { t } = useTranslation();
        const theme = useTheme();

        const isDrawer = variant === "drawer";
        const isExpanded = isDrawer || isOpen;
        const toggleTitle = isDrawer
                ? t("ai-chat-close") || "Close"
                : isOpen
                        ? t("ai-chat-history-toggle-close")
                        : t("ai-chat-history-toggle-open");

        return (
                <Box
                        sx={{
                                width: isDrawer ? "100%" : historyWidth,
                                minWidth: isDrawer ? "100%" : historyWidth,
                                maxWidth: isDrawer ? "100%" : historyWidth,
                                transition: isDrawer ? "none" : "width 0.3s ease",
                                borderRight: isDrawer ? "none" : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                borderBottom: isDrawer ? `1px solid ${alpha(theme.palette.divider, 0.2)}` : "none",
                                backgroundColor: alpha(
                                        theme.palette.background.paper,
                                        isDrawer ? 0.95 : 0.7
                                ),
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                height: isDrawer ? "100%" : "auto",
                        }}
                >
                        <Box
                                sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: isExpanded ? "space-between" : "center",
                                        px: 1.5,
                                        py: 1,
                                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        backgroundColor: isDrawer
                                                ? alpha(theme.palette.background.paper, 0.85)
                                                : "transparent",
                                }}
                        >
                                {isExpanded && (
                                        <Box display="flex" alignItems="center" gap={1}>
                                                <HistoryIcon fontSize="small" color="primary" />
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                        {t("ai-chat-history-title")}
                                                </Typography>
                                        </Box>
                                )}
                                <Tooltip title={toggleTitle} arrow>
                                        <IconButton
                                                size="small"
                                                onClick={() => {
                                                        if (isDrawer) {
                                                                onToggle(false);
                                                                return;
                                                        }
                                                        onToggle(!isOpen);
                                                }}
                                        >
                                                {isDrawer ? (
                                                        <CloseIcon fontSize="small" />
                                                ) : isOpen ? (
                                                        <MenuOpenIcon fontSize="small" />
                                                ) : (
                                                        <MenuIcon fontSize="small" />
                                                )}
                                        </IconButton>
                                </Tooltip>
                        </Box>
                        {isExpanded ? (
                                <>
                                        <Box
                                                sx={{
                                                        p: 1.5,
                                                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                                                }}
                                        >
                                                <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<AddIcon fontSize="small" />}
                                                        onClick={onCreateChat}
                                                        disabled={disableInteractions}
                                                >
                                                        {t("ai-chat-history-new-chat")}
                                                </Button>
                                        </Box>
                                        <Box sx={{ flex: 1, overflowY: "auto" }}>
                                                {sessions.length === 0 ? (
                                                        <Box sx={{ p: 2 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                        {t("ai-chat-history-empty")}
                                                                </Typography>
                                                        </Box>
                                                ) : (
                                                        <List disablePadding>
                                                                {sessions.map((session) => {
                                                                        const isActive = session.id === selectedChatId;
                                                                        const sessionTitle =
                                                                                session.title ||
                                                                                formatHistoryDateTime(session.createdAt) ||
                                                                                t("ai-chat-history-untitled");
                                                                        const lastMessageText = stripMarkdown(
                                                                                session.lastMessage ||
                                                                                t("ai-chat-history-no-messages"));
                                                                        const lastMessageTime = formatHistoryDateTime(
                                                                                session.lastMessageTime ||
                                                                                        session.updatedAt ||
                                                                                        session.createdAt
                                                                        );

                                                                        return (
                                                                                <ListItem
                                                                                        key={session.id}
                                                                                        disablePadding
                                                                                        secondaryAction={(
                                                                                                <Tooltip
                                                                                                        title={t("ai-chat-history-delete-chat")}
                                                                                                        arrow
                                                                                                >
                                                                                                        <span>
                                                                                                                <IconButton
                                                                                                                        edge="end"
                                                                                                                        size="small"
                                                                                                                        onClick={(event) => {
                                                                                                                                event.stopPropagation();
                                                                                                                                onDeleteChat(session.id);
                                                                                                                        }}
                                                                                                                        disabled={disableInteractions}
                                                                                                                >
                                                                                                                        <DeleteIcon fontSize="small" />
                                                                                                                </IconButton>
                                                                                                        </span>
                                                                                                </Tooltip>
                                                                                        )}
                                                                                >
                                                                                        <ListItemButton
                                                                                                selected={isActive}
                                                                                                onClick={() => {
                                                                                                        if (session.id !== selectedChatId) {
                                                                                                                onSelectChat(session.id);
                                                                                                        }

                                                                                                        if (isDrawer) {
                                                                                                                onToggle(false);
                                                                                                        }
                                                                                                }}
                                                                                                sx={{ alignItems: "flex-start", py: 1.25, px: 1.5 }}
                                                                                        >
                                                                                                <ListItemText
                                                                                                        primary={
                                                                                                                <Box
                                                                                                                        display="flex"
                                                                                                                        alignItems="center"
                                                                                                                        justifyContent="space-between"
                                                                                                                        gap={1}
                                                                                                                >
                                                                                                                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                                                                                                                                {sessionTitle}
                                                                                                                        </Typography>
                                                                                                                        {lastMessageTime && (
                                                                                                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                                                                                                        {lastMessageTime}
                                                                                                                                </Typography>
                                                                                                                        )}
                                                                                                                </Box>
                                                                                                        }
                                                                                                        secondary={
                                                                                                                <Typography
                                                                                                                        variant="body2"
                                                                                                                        color="text.secondary"
                                                                                                                        sx={{
                                                                                                                                display: "-webkit-box",
                                                                                                                                WebkitLineClamp: 2,
                                                                                                                                WebkitBoxOrient: "vertical",
                                                                                                                                overflow: "hidden",
                                                                                                                        }}
                                                                                                                >
                                                                                                                        {lastMessageText}
                                                                                                                </Typography>
                                                                                                        }
                                                                                                        secondaryTypographyProps={{ component: "div" }}
                                                                                                />
                                                                                        </ListItemButton>
                                                                                </ListItem>
                                                                        );
                                                                })}
                                                        </List>
                                                )}
                                        </Box>
                                </>
                        ) : (
                                <Box
                                        sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 1.5,
                                                py: 2,
                                        }}
                                >
                                        <Tooltip title={t("ai-chat-history-new-chat")} arrow>
                                                <span>
                                                        <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={onCreateChat}
                                                                disabled={disableInteractions}
                                                        >
                                                                <AddIcon fontSize="small" />
                                                        </IconButton>
                                                </span>
                                        </Tooltip>
                                        <Tooltip title={t("ai-chat-history-title")} arrow>
                                                <HistoryIcon fontSize="small" color="primary" />
                                        </Tooltip>
                                </Box>
                        )}
                </Box>
        );
};

export default ChatHistory;

